/**
 * `/claims` (§16.2 "Claims queue"): age, deadline, missing information, risk flags and the external verification
 * result per claim; filters by status / fulfillment type / overdue / risk flag. Identity stays masked (§16.3).
 * The "needs me now" strip (overdue, past review target, awaiting a second approver, missing information) sits
 * above the table so the queue reads by urgency first (D-2605).
 */
import { useQuery } from '@tanstack/react-query';
import { ClaimRiskFlag, ClaimStatus, FulfillmentType, type ClaimQueueRowDto } from '@windrop/contracts';
import { Button, useIntl } from '@windrop/ui';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { DataTable, type Column } from '../../components/DataTable';
import { EmptyState, ErrorState } from '../../components/EmptyState';
import { Chip, ClearFilters, FilterBar, FilterField, FilterSelect } from '../../components/FilterBar';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge, ToneBadge, humanizeStatus } from '../../components/StatusBadge';
import { claimsApi } from '../../lib/api/claims';
import { isApiError } from '../../lib/api/client';

export const ageText = (seconds: number): string => {
  if (seconds < 3600) return `${Math.max(1, Math.round(seconds / 60))} min`;
  if (seconds < 86_400) return `${Math.round(seconds / 3600)} h`;
  return `${Math.round(seconds / 86_400)} d`;
};

export const RISK_LABEL: Record<string, string> = {
  NAME_MISMATCH: 'Name mismatch — review, never auto-reject',
  DUPLICATE_ID_NUMBER: 'ID number seen on another claim',
  DUPLICATE_PAYOUT_DESTINATION: 'Payout destination seen on another claim',
  PROVIDER_OUTAGE: 'Verification provider delay (platform-caused)',
  HIGH_VALUE: 'High value — dual approval',
  APPEAL_OPEN: 'Appeal under review',
};

const REVIEW_STATUSES = ['SUBMITTED', 'VERIFICATION_PENDING', 'MORE_INFO', 'VERIFIED'];

export function ClaimsQueuePage() {
  const intl = useIntl();
  const [status, setStatus] = useState('');
  const [type, setType] = useState('');
  const [risk, setRisk] = useState('');
  const [overdue, setOverdue] = useState(false);
  const query = useQuery({
    queryKey: ['claims', { status, type, risk, overdue }],
    queryFn: () =>
      claimsApi.list({
        status: status ? (status as ClaimStatus) : undefined,
        fulfillmentType: type ? (type as FulfillmentType) : undefined,
        riskFlag: risk ? (risk as ClaimRiskFlag) : undefined,
        overdue: overdue || undefined,
        limit: 100,
      }),
  });
  const now = Date.now();
  const items = useMemo(() => query.data?.items ?? [], [query.data]);
  const needs = useMemo(
    () => ({
      overdue: items.filter((r) => Date.parse(r.claimDeadline) < now).length,
      pastTarget: items.filter(
        (r) => r.ageSeconds > 86_400 && ['SUBMITTED', 'VERIFICATION_PENDING', 'VERIFIED'].includes(r.status),
      ).length,
      secondApproval: items.filter((r) => r.awaitingSecondApproval).length,
      missing: items.filter((r) => r.missingItems.length > 0).length,
    }),
    [items, now],
  );
  const columns = useMemo<Column<ClaimQueueRowDto>[]>(
    () => [
      {
        id: 'win',
        header: 'Win / prize',
        sortValue: (r) => r.winIdDisplay,
        filterValue: (r) => `${r.winIdDisplay} ${r.prizeName} ${r.lotteryTitle} ${r.legalNameMasked ?? ''}`,
        cell: (r) => (
          <>
            <Link to={`/claims/${r.claimId}`}>
              <strong>{r.winIdDisplay}</strong>
            </Link>
            <br />
            <span className="wd-muted">
              {r.prizeName} · {intl.formatMoney(r.prizeValue.amountMinor, r.prizeValue.currency)} ·{' '}
              {r.lotteryTitle}
            </span>
          </>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        sortValue: (r) => r.status,
        cell: (r) => <StatusBadge kind="ClaimStatus" value={r.status} />,
      },
      {
        id: 'age',
        header: 'Age',
        sortValue: (r) => r.ageSeconds,
        cell: (r) => (
          <span
            data-testid="claim-age"
            data-overdue={r.ageSeconds > 86_400 && REVIEW_STATUSES.includes(r.status) ? 'true' : undefined}
          >
            {ageText(r.ageSeconds)}
            {r.ageSeconds > 86_400 && ['SUBMITTED', 'VERIFICATION_PENDING', 'VERIFIED'].includes(r.status) ? (
              <>
                {' '}
                <ToneBadge tone="error">Past review target</ToneBadge>
              </>
            ) : null}
          </span>
        ),
      },
      {
        id: 'deadline',
        header: 'Deadline',
        sortValue: (r) => r.claimDeadline,
        cell: (r) => (
          <span data-testid="claim-deadline">
            <time dateTime={r.claimDeadline}>{intl.formatDateTime(r.claimDeadline)}</time>
            {Date.parse(r.claimDeadline) < now ? (
              <>
                {' '}
                <ToneBadge tone="error">Overdue</ToneBadge>
              </>
            ) : null}
          </span>
        ),
      },
      {
        id: 'missing',
        header: 'Missing information',
        cell: (r) =>
          r.missingItems.length ? (
            <ul data-testid="claim-missing">
              {r.missingItems.map((m) => (
                <li key={m.field}>
                  {humanizeStatus(m.field)}: {m.reason}
                </li>
              ))}
            </ul>
          ) : (
            <span className="wd-muted">—</span>
          ),
      },
      {
        id: 'risk',
        header: 'Risk flags',
        filterValue: (r) => r.riskFlags.join(' '),
        cell: (r) =>
          r.riskFlags.length ? (
            r.riskFlags.map((f) => (
              <ToneBadge key={f} tone={f === 'APPEAL_OPEN' || f === 'HIGH_VALUE' ? 'info' : 'warning'}>
                {humanizeStatus(f)}
              </ToneBadge>
            ))
          ) : (
            <span className="wd-muted">none</span>
          ),
      },
      {
        id: 'verification',
        header: 'External verification',
        sortValue: (r) => r.verification.decision ?? '',
        cell: (r) =>
          r.verification.decision ? (
            <>
              <StatusBadge kind="VerificationDecision" value={r.verification.decision} />{' '}
              {r.verification.providerReference ? (
                <code className="wd-muted">{r.verification.providerReference}</code>
              ) : null}
            </>
          ) : (
            <span className="wd-muted">not recorded</span>
          ),
      },
      {
        id: 'name',
        header: 'Legal name',
        cell: (r) => <code>{r.legalNameMasked ?? '—'}</code>,
        defaultHidden: true,
      },
      {
        id: 'type',
        header: 'Fulfillment',
        sortValue: (r) => r.fulfillmentType,
        cell: (r) => (
          <>
            {humanizeStatus(r.fulfillmentType)}
            {r.requiresDualApproval ? <ToneBadge tone="info">Dual approval</ToneBadge> : null}
            {r.awaitingSecondApproval ? <ToneBadge tone="warning">Awaiting 2nd approver</ToneBadge> : null}
          </>
        ),
      },
    ],
    [intl, now],
  );
  const clear = () => {
    setStatus('');
    setType('');
    setRisk('');
    setOverdue(false);
  };
  const filtersActive = Boolean(status || type || risk || overdue);
  return (
    <>
      <PageHeader
        breadcrumb={[{ label: 'Claims queue' }]}
        title="Claims queue"
        description="Review claims, record the external verification result, request precise missing items, approve (only once verified) or reject with a safe reason. Identity and payout data stay masked."
        actions={
          <Button variant="secondary" href="/fulfillment">
            Fulfillment
          </Button>
        }
      >
        {query.data ? (
          <div className="wdc-status-strip" aria-label="What needs attention">
            <button
              type="button"
              className="wdc-status-strip__item"
              data-tone={needs.overdue ? 'error' : undefined}
              onClick={() => setOverdue(true)}
            >
              <strong>{needs.overdue}</strong> past the claim deadline
            </button>
            <span className="wdc-status-strip__item" data-tone={needs.pastTarget ? 'warning' : undefined}>
              <strong>{needs.pastTarget}</strong> past the one-business-day review target
            </span>
            <span className="wdc-status-strip__item" data-tone={needs.secondApproval ? 'warning' : undefined}>
              <strong>{needs.secondApproval}</strong> awaiting a second approver
            </span>
            <span className="wdc-status-strip__item">
              <strong>{needs.missing}</strong> waiting on missing information
            </span>
          </div>
        ) : null}
      </PageHeader>
      <FilterBar
        end={
          <>
            <Chip pressed={overdue} onClick={() => setOverdue((v) => !v)}>
              Overdue only
            </Chip>
            <ClearFilters active={filtersActive} onClick={clear} />
          </>
        }
      >
        <FilterField label="Status" htmlFor="claims-status">
          <FilterSelect
            id="claims-status"
            aria-label="Status"
            value={status}
            onChange={setStatus}
            options={[
              { value: '', label: 'Open review' },
              ...ClaimStatus.options.map((s) => ({ value: s, label: humanizeStatus(s) })),
            ]}
          />
        </FilterField>
        <FilterField label="Fulfillment type" htmlFor="claims-type">
          <FilterSelect
            id="claims-type"
            aria-label="Fulfillment type"
            value={type}
            onChange={setType}
            options={[
              { value: '', label: 'All types' },
              ...FulfillmentType.options.map((s) => ({ value: s, label: humanizeStatus(s) })),
            ]}
          />
        </FilterField>
        <FilterField label="Risk flag" htmlFor="claims-risk">
          <FilterSelect
            id="claims-risk"
            aria-label="Risk flag"
            value={risk}
            onChange={setRisk}
            options={[
              { value: '', label: 'Any risk' },
              ...ClaimRiskFlag.options.map((s) => ({ value: s, label: humanizeStatus(s) })),
            ]}
          />
        </FilterField>
      </FilterBar>
      {query.isError ? (
        <ErrorState
          title="The claims queue did not load"
          body="Retry in a moment."
          reference={isApiError(query.error) ? query.error.supportReference : undefined}
          action={<Button onClick={() => query.refetch()}>Retry</Button>}
        />
      ) : (
        <DataTable<ClaimQueueRowDto>
          caption="Claims awaiting review, sorted by deadline"
          columns={columns}
          rows={items}
          rowKey={(r) => r.claimId}
          loading={query.isPending}
          initialSort={{ columnId: 'deadline', direction: 'asc' }}
          filterable
          filterPlaceholder="Filter by win ID, prize, lottery or name"
          emptyState={
            <EmptyState
              title="No claims match"
              body="Nothing is waiting for review with these filters."
              action={<Button onClick={clear}>Clear filters</Button>}
            />
          }
        />
      )}
    </>
  );
}
